import React from 'react'
import styled from 'styled-components'

import Card from '@jarvis.oddle/core/components/Card'
import Flex from '@jarvis.oddle/core/components/Flex'
import Input from '@jarvis.oddle/core/components/Input'
import { H4, P } from '@jarvis.oddle/core/components/Typo'
import Divider from '@jarvis.oddle/core/components/Divider'
import DivButton from '@jarvis.oddle/core/components/DivButton'
import IconDelete from '@jarvis.oddle/core/components/Icons/IconDelete'
import { colors, margin, marginSizes } from '@jarvis.oddle/core/theme/default'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import Form, {
  FormContext,
  makeFormField,
  withFormContext
} from '@jarvis.oddle/core/components/Form'
import {
  InlineNotification,
  notificationStates
} from '@jarvis.oddle/core/components/InlineNotification'

import useAppSelector from 'src/hooks/useAppSelector'
import { selectOrder } from 'src/redux/order'
import {
  checkPromoCodeAction,
  togglePromotionAction
} from 'src/redux/order/action'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import { useRouter } from 'next/router'
import { parseLocale } from 'src/utils/localisation'
import { stackOptions , isItemPromotion } from 'src/utils/promotion'
import { isObjectEmpty } from 'src/utils/object'
import { isEmpty } from 'src/utils/string'

const AppliedPromoCodeContainer = styled(Card).attrs(() => ({
  borderRadius: 'small',
  padding: 'medium'
}))`
  border: 1px ${(props) => props.theme.colorLinesAndBorders || colors.separator}
    solid;
  margin-bottom: ${margin[marginSizes.small]};
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:last-child {
    margin-bottom: 0;
  }

  ${(props) =>
    props.disabled
      ? `
    color: ${props.theme.colorLinesAndBorders || colors.separator};
  `
      : ''}
`

const StyledSubmitButton = styled(Form.SubmitButton)`
  width: 100%;
  height: 100%;
`

const PromotionCodeErrorMessage = withFormContext(
  ({ form: { errors, touched } }) =>
    errors.promoCode && touched.promoCode ? (
      <Form.FieldError>{errors.promoCode}</Form.FieldError>
    ) : null
)

const FormInput = makeFormField(({ fieldError, fieldTouched, ...props }) => (
  <Flex
    flexBasis='100%'
    marginBottom={marginSizes.medium}
    marginRight={marginSizes.medium}
  >
    <Input {...props} withError={fieldError && fieldTouched} controlled />
  </Flex>
))

const PromoCodePromotion = ({
  goToNextModal
}: {
  goToNextModal: (modalName, passingProps) => void
}): JSX.Element => {
  const dispatch = useAppDispatch()
  const { t } = React.useContext(TranslationContext)
  const metaState = useAppSelector((state) => state['@meta'].detailMenuSite)
  const selectedPromotions = useAppSelector(selectOrder).selectedPromotions.data
  const router = useRouter()
  const { country } = parseLocale(router.query.locale as string)
  const { enableUniversalPromotion } = metaState?.data?.features || {}
  const { unStackable, selectedPromoCodes } = selectedPromotions.reduce(
    (acc, selectedPromo) => {
      if (selectedPromo.stackableOption === stackOptions.nonStackable) {
        acc.unStackable = selectedPromo
      }

      if (selectedPromo.isPromoCodePromotion) {
        acc.selectedPromoCodes.push(selectedPromo)
      }

      return acc
    },
    { unStackable: undefined, selectedPromoCodes: [] }
  )

  const applyPromoCode = async (
    { promoCode },
    { setSubmitting, setFieldError }
  ) => {
    setFieldError('general', null)

    const formattedPromoCode =
      typeof promoCode === 'string' ? promoCode.toUpperCase().trim() : ''
    const applyPromoCode = await dispatch(
      checkPromoCodeAction({
        code: formattedPromoCode
      })
    )
    if (applyPromoCode.meta.requestStatus === 'fulfilled' && isEmpty(applyPromoCode.payload?.payload?.error)) {
      if (isItemPromotion(applyPromoCode.payload.payload.promotion.type)) {
        goToNextModal('CheckoutPromotionItemModal', {
          promotion: applyPromoCode.payload.payload.promotion
        })
      } else {
        dispatch(
          togglePromotionAction({ promotion: applyPromoCode.payload.payload.promotion })
        )
      }
    } else {
      setFieldError('promoCode', t(applyPromoCode.payload.payload.error))
    }
    setSubmitting(false)
  }

  return (
    <React.Fragment>
      <Form onSubmit={applyPromoCode}>
        <H4 normalised marginBottom={marginSizes.small} bold>
          {t('Have a promo code?')}
        </H4>
        <Flex alignItems='center'>
          <FormInput
            name='promoCode'
            label={t('Enter your promo code')}
            textTransform='uppercase'
          />
          <FormContext>
            {({ isSubmitting }) => (
              <Flex
                marginBottom={marginSizes.medium}
                flexShrink={0}
                flexBasis='100px'
              >
                <StyledSubmitButton
                  data-testid='button.submit'
                  disabled={isSubmitting || !isObjectEmpty(unStackable)}
                  secondary
                >
                  {t('Apply')}
                </StyledSubmitButton>
              </Flex>
            )}
          </FormContext>
        </Flex>
        <PromotionCodeErrorMessage />
      </Form>
      {country === 'SG' && enableUniversalPromotion && (
        <InlineNotification lightTheme state={notificationStates.informative}>
          {t(
            'Looking to use your Oddle Eats vouchers? Skip to payments in the checkout page and select your vouchers instead.'
          )}
        </InlineNotification>
      )}
      {selectedPromoCodes.length > 0 && (
        <React.Fragment>
          <Divider />
          <P bold>{t('Applied Promo Codes')}</P>
          {selectedPromoCodes.map((promotion) => (
            <AppliedPromoCodeContainer
              disabled={unStackable && unStackable.id !== promotion.id}
            >
              <Flex flexDirection='column'>
                <P normalised bold>
                  {promotion.name}
                </P>
                <P normalised>{promotion.description}</P>
              </Flex>
              <DivButton
                data-testid='button.delete'
                onClick={() => dispatch(togglePromotionAction({ promotion }))}
              >
                <IconDelete />
              </DivButton>
            </AppliedPromoCodeContainer>
          ))}
        </React.Fragment>
      )}
    </React.Fragment>
  )
}

export default PromoCodePromotion
